import {useEffect, useMemo} from "react";
import {App, Form, Input, Modal, Select} from "antd";

import * as usersApi from "@modules/users/usersApi.js";

const ROLE_OPTIONS = {
	superadmin: [
		{value: "superadmin", label: "Superadmin"},
		{value: "admin", label: "Admin"},
		{value: "operator", label: "Operatore"}
	],
	admin: [
		{value: "admin", label: "Admin"},
		{value: "operator", label: "Operatore"}
	],
	operator: []
};

function apiError(e)
{
	const code = e?.response?.data?.error;
	const map = {
		items_exceed_creator: "Hai assegnato voci che non possiedi",
		items_required: "Assegna almeno una voce a menu",
		items_required_on_activate: "Per riattivare assegna almeno una voce + landing",
		username_taken: "Username gia' in uso",
		weak_password: "Password troppo debole (min 10 caratteri)",
		role_not_creatable: "Non puoi creare questo ruolo",
		role_not_assignable: "Non puoi assegnare questo ruolo",
		last_superadmin: "Non puoi declassare l'ultimo superadmin",
		self_escalation_blocked: "Non puoi modificare i tuoi permessi"
	};
	return map[code] || "Operazione non riuscita";
}

export default function UserFormModal({open, mode, record, actor, catalog, onClose, onSaved})
{
	const {message} = App.useApp();
	const [form] = Form.useForm();
	const isEdit = mode === "edit";
	const isReactivate = mode === "reactivate";

	const roleOptions = ROLE_OPTIONS[actor.role] || [];

	// Voci assegnabili = catalogo.assignable ∩ voci dell'attore (superadmin: tutto l'assignable).
	const assignable = useMemo(() =>
	{
		const cat = catalog.assignable || [];
		if (actor.role === "superadmin") return cat;
		const mine = new Set(actor.allowedMenuItems || []);
		return cat.filter(k => mine.has(k));
	}, [catalog, actor]);

	useEffect(() =>
	{
		if (!open) return;
		if (isEdit || isReactivate)
		{
			form.setFieldsValue({
				username: record?.username,
				displayName: record?.displayName,
				role: record?.role,
				allowedMenuItems: record?.allowedMenuItems || [],
				landingMenuItem: record?.landingMenuItem || undefined,
				password: ""
			});
		}
		else
		{
			form.resetFields();
			form.setFieldsValue({role: roleOptions[0]?.value, allowedMenuItems: []});
		}
	}, [open, record, isEdit, isReactivate, form, roleOptions]);

	const watchedItems = Form.useWatch("allowedMenuItems", form) || [];
	const watchedRole = Form.useWatch("role", form);
	const isSuperadminRole = watchedRole === "superadmin";

	const applyPreset = (presetName) =>
	{
		const preset = (catalog.presets || {})[presetName] || [];
		form.setFieldsValue({allowedMenuItems: preset.filter(k => assignable.includes(k))});
	};

	const submit = async () =>
	{
		let vals;
		try { vals = await form.validateFields(); }
		catch { return; }

		try
		{
			if (isEdit)
			{
				const payload = {
					displayName: vals.displayName,
					role: vals.role,
					allowedMenuItems: vals.allowedMenuItems,
					landingMenuItem: vals.landingMenuItem
				};
				if (vals.password) payload.password = vals.password;
				const {cascade} = await usersApi.updateUser(record.id || record._id, payload);
				message.success("Utente aggiornato");
				if (cascade?.deactivated)
				{
					const n = cascade.deactivated;
					message.warning(
						n === 1
							? "1 utente sottoposto è stato disattivato automaticamente (rimasto senza voci a menu in comune)"
							: `${n} utenti sottoposti sono stati disattivati automaticamente (rimasti senza voci a menu in comune)`,
						6
					);
				}
			}
			else if (isReactivate)
			{
				await usersApi.activateUser(record.id || record._id, {
					allowedMenuItems: vals.allowedMenuItems,
					landingMenuItem: vals.landingMenuItem
				});
				message.success("Utente riattivato");
			}
			else
			{
				await usersApi.createUser({
					username: vals.username,
					password: vals.password,
					displayName: vals.displayName,
					role: vals.role,
					allowedMenuItems: vals.allowedMenuItems,
					landingMenuItem: vals.landingMenuItem
				});
				message.success("Utente creato");
			}
			onSaved();
		}
		catch (e)
		{
			message.error(apiError(e));
		}
	};

	const title = isEdit ? "Modifica utente" : (isReactivate ? "Riattiva utente" : "Nuovo utente");

	return (
		<Modal open={open} title={title} onOk={submit} onCancel={onClose} okText="Salva" destroyOnHidden>
			<Form form={form} layout="vertical">
				{!isEdit && !isReactivate && (
					<Form.Item name="username" label="Username" rules={[{required: true, message: "Username obbligatorio"}]}>
						<Input autoComplete="off"/>
					</Form.Item>
				)}
				<Form.Item name="displayName" label="Nome visualizzato" rules={[{required: true, message: "Nome obbligatorio"}]}>
					<Input/>
				</Form.Item>
				<Form.Item name="role" label="Ruolo" rules={[{required: true}]}>
					<Select options={roleOptions} disabled={isReactivate}/>
				</Form.Item>

				{!isSuperadminRole && (
					<>
						<Form.Item label="Preset (opzionale)">
							<Select
								placeholder="Applica un preset…"
								allowClear
								onChange={(v) => v && applyPreset(v)}
								options={Object.keys(catalog.presets || {}).map(p => ({value: p, label: p}))}
							/>
						</Form.Item>
						<Form.Item name="allowedMenuItems" label="Voci a menu" rules={[{required: true, type: "array", min: 1, message: "Almeno una voce"}]}>
							<Select mode="multiple" options={assignable.map(k => ({value: k, label: k}))}/>
						</Form.Item>
						<Form.Item name="landingMenuItem" label="Pagina di atterraggio" rules={[{required: true, message: "Scegli la landing"}]}>
							<Select options={watchedItems.map(k => ({value: k, label: k}))}/>
						</Form.Item>
					</>
				)}

				{!isReactivate && (
					<Form.Item
						name="password"
						label={isEdit ? "Nuova password (vuoto = invariata)" : "Password"}
						rules={isEdit ? [] : [{required: true, min: 10, message: "Minimo 10 caratteri"}]}
					>
						<Input.Password autoComplete="new-password"/>
					</Form.Item>
				)}
			</Form>
		</Modal>
	);
}
